import useSWR, { mutate } from "swr";
import useSWRMutation from "swr/mutation";
import { softValidateForm, STATUS_PROGRESSION } from "protocol-setup/helpers";
import { fetchWithAuth, postWithAuth } from "./makeFetch";
import {
  leadGroupLov,
  leadGroupProtocolLov,
  getPersonRole,
  raveStudyGroupsLov,
  countries,
  protocolPath,
  saveProtocolInfoPath,
  saveProtocolInfoPathB,
  protocolSetupPath,
  requestBrowserPath,
  validateProtocolSetupRequest,
  esysDataServicePath
} from "./constants";

const authFetcher = async (url) => {
  try {
    return await fetchWithAuth(url);
  } catch (error) {
    return error;
  }
};

const authPostFetcher = async ([url, payload]) => {
  if (url) {
    try {
      return await postWithAuth(url, payload);
    } catch (error) {
      return error;
    }
  }
  return null;
};

export function usePSRSbyProtocolNumber(protocolNumber) {
  const url = protocolNumber ? `${protocolPath}/number/${protocolNumber}/psrs` : null;
  const { data, error, isLoading } = useSWR([url, {}], authPostFetcher, {
    revalidateOnFocus: false
  });

  return {
    psrList: data?.data || [],
    isLoading,
    error,
    mutatePSRS: mutate
  };
}

export function useCountriesLov() {
  const { data, error, isLoading } = useSWR(countries, authFetcher, {
    revalidateOnFocus: false
  });

  const countryList = (data || [])
    .map((country) => ({
      label: `${country.code}-${country.name}`,
      value: country.name,
      id: String(country.id),
      code: country.code
    }))
    .sort((a, b) => {
      if (a.code === 'US') return -1;
      if (b.code === 'US') return 1;
      return 0;
    });

  return {
    countryList,
    isLoading,
    error,
    mutateCountriesLov: mutate
  };
}

export function useLeadGroupLOVs() {
  const { data, error, isLoading } = useSWR(leadGroupLov, authFetcher, {
    revalidateOnFocus: false
  });

  return {
    leadGroupList: data || [],
    isLoading,
    error,
    mutateLeadGroupLOVs: mutate
  };
}

export function useProtocolLOVs(lpo) {
  const { data, error, isLoading } = useSWR(lpo ? leadGroupProtocolLov(lpo) : null, authFetcher, {
    revalidateOnFocus: false
  });

  return {
    protocolList: data || [],
    isLoading,
    error,
    mutateProtocolLOVs: mutate
  };
}

export function raveStudyGroupsLOVs(lpo) {
  const { data, error, isLoading } = useSWR(lpo ? raveStudyGroupsLov(lpo) : null, authFetcher, {
    revalidateOnFocus: false
  });

  return {
    raveStudyGroups: data || [],
    isLoading,
    error,
    mutateRaveStudyGroupsLOVs: mutate
  };
}

export function useContactList(lpo = "CTSU") {
  const payload = {
    filter: [
      {
        field: "roleName",
        operation: "in",
        value: ["Sys Admin", "LPO Registrar", "CTMS Registrar"]
      },
      {
        field: "lpo",
        operation: "=",
        value: lpo
      }
    ],
    first: 0,
    isMetaRequired: false,
    size: 0,
    sort: []
  };

  const { data, error, isLoading } = useSWR([getPersonRole, payload], authPostFetcher, {
    revalidateOnFocus: false
  });
  const d = data?.data || [];

  // remove duplicate ctepPersonId
  const contactList = {
    data: []
  };

  if (Array.isArray(d)) {
    const lookup = {};
    for (let i = 0; i < d.length; i++) {
      const item = d[i];
      if (item.ctepPersonId && !lookup[item.ctepPersonId]) {
        lookup[item.ctepPersonId] = true;
        contactList.data.push(item);
      }
    }
  }

  const resetContactList = () => {
    mutate(null, { revalidate: false });
  };

  return {
    contactList,
    isLoading,
    error,
    mutateContactList: mutate,
    resetContactList
  };
}
export function useRegContactList(lpo = "CTSU") {
  const payload = {
    filter: [
      {
        field: "roleName",
        operation: "in",
        value: ["Sys Admin", "LPO Protocol Specialist"]
      },
      {
        field: "lpo",
        operation: "=",
        value: lpo
      }
    ],
    first: 0,
    isMetaRequired: false,
    size: 0,
    sort: []
  };

  const { data, error, isLoading } = useSWR([getPersonRole, payload], authPostFetcher, {
    revalidateOnFocus: false
  });
  const d = data?.data || [];

  // remove duplicate ctepPersonId
  const contactList = {
    data: []
  };

  if (Array.isArray(d)) {
    const lookup = {};
    for (let i = 0; i < d.length; i++) {
      const item = d[i];
      if (item.ctepPersonId && !lookup[item.ctepPersonId]) {
        lookup[item.ctepPersonId] = true;
        contactList.data.push(item);
      }
    }
  }

  const resetContactList = () => {
    mutate(null, { revalidate: false });
  };

  return {
    contactList,
    isLoading,
    error,
    mutateContactList: mutate,
    resetContactList
  };
}

// New hook for OPEN Admin contacts
export function useOpenAdminContactList(lpo = "CTSU") {
  const payload = {
    filter: [
      {
        field: "roleName",
        operation: "in",
        value: ["OPEN Administrator"] // Adjust role names as needed
      },
      {
        field: "lpo",
        operation: "=",
        value: lpo
      }
    ],
    first: 0,
    isMetaRequired: false,
    size: 0,
    sort: []
  };

  const { data, error, isLoading } = useSWR(
    [getPersonRole, payload, "open-admin"], // Added unique identifier to prevent cache conflicts
    authPostFetcher,
    {
      revalidateOnFocus: false
    }
  );

  const d = data?.data || [];

  // Remove duplicate ctepPersonId
  const openAdminContactList = {
    data: []
  };
  if (Array.isArray(d)) {
    const lookup = {};
    for (let i = 0; i < d.length; i++) {
      const item = d[i];
      if (item.ctepPersonId && !lookup[item.ctepPersonId]) {
        lookup[item.ctepPersonId] = true;
        openAdminContactList.data.push(item);
      }
    }
  }

  const resetOpenAdminContactList = () => {
    mutate(null, { revalidate: false });
  };

  return {
    openAdminContactList,
    isLoading,
    error,
    mutateOpenAdminContactList: mutate,
    resetOpenAdminContactList
  };
}

let protocolSetupData = null;

// Mutate all protocol-setup-data related cache entries
// Only mutate the specific cache key if we have rqNum
export function setProtocolSetupData(data, rqNum = null, part = "a") {
  protocolSetupData = data;
  if (rqNum) {
    const specificKey = `protocol-setup-data-${rqNum}-${part}`;
    mutate(specificKey, data, { revalidate: false });
  } else {
    // Mutate all protocol-setup-data related cache entries
    mutate((key) => typeof key === "string" && key.startsWith("protocol-setup-data"), data, { revalidate: false });
  }
}

function mergeProtocolData(data) {
  if (data) {
    let newData = {};
    if (data?.protDto?.formPartType === "OPEN_RAVE") {
      const { openDto, protDto, raveDto } = data || {};
      newData = { ...openDto, ...protDto, ...raveDto };
    } else {
      const { protDto, regDto, docDtoList } = data || {};
      const attachPSR = [];
      const attachInProgress = [];
      if (docDtoList) {
        const b = docDtoList
          .map(str => JSON.parse(str))
          .flat();
        b.forEach((c) => {
          if (c.ALTERNATE_CODE === 'REGULATORY') {
            attachPSR.push({
              name: c.DOC_FILE_NAME,
              id: c.DOCUMENT_ID,
              guid: c.DOCUMENT_GUID
            })
          } else {
            attachInProgress.push({
              name: c.DOC_FILE_NAME,
              id: c.DOCUMENT_ID,
              guid: c.DOCUMENT_GUID
            });
          }
        });
      }
      newData = { ...protDto, ...regDto, attachPSR, attachInProgress };
    }
    return newData;
  }
  return null;
}

// SWR middleware to flatten data
const flattenMiddleware = (useSWRNext) => (key, fetcher, config) => {
  return useSWRNext(
    key,
    async (...args) => {
      const data = await fetcher(...args);
      return mergeProtocolData(data);
    },
    config
  );
};

export function useProtocolSetupData(rqNum = null, part = "a", history) {
  const shouldFetch = !!rqNum;
  const cacheKey = shouldFetch ? `protocol-setup-data-${rqNum}-${part}` : "protocol-setup-data-manual";

  const { data, error } = useSWR(
    shouldFetch ? cacheKey : null,
    shouldFetch ? () => fetchWithAuth(`${part === "a" ? saveProtocolInfoPath : saveProtocolInfoPathB}/${rqNum}`) : null,
    {
      use: [flattenMiddleware],
      onSuccess: (d) => setProtocolSetupData(d),
      onError: (e) => {
        if (history) {
          history.push("/protocol-setup/browser");
        }
      }
    }
  );

  const { trigger, isMutating } = useSWRMutation(
    shouldFetch ? `${part === "a" ? saveProtocolInfoPath : saveProtocolInfoPathB}/${rqNum}` : null,
    fetchWithAuth,
    {
      use: [flattenMiddleware],
      onSuccess: (d) => setProtocolSetupData(d),
      onError: (e) => {
        if (history) {
          return history.push("/protocol-setup/browser");
        }
        return null;
      }
    }
  );

  return {
    data: data || protocolSetupData,
    setProtocolSetupData,
    trigger,
    isMutating,
    isLoading: !error && !data && shouldFetch,
    error
  };
}

let requestBarData = null;

export function setRequestBarData(data) {
  requestBarData = data;
  mutate("request-bar-data");
}

export function useRequestBarData() {
  const { data } = useSWR("request-bar-data", () => requestBarData);
  return {
    requestBarData: data,
    setRequestBarData
  };
}

export function useValidateRequest(psData = {}, formType = "OPEN_RAVE", softValidationFields = []) {
  const fields = [...softValidationFields];

  if (psData && formType === "REGULATORY" && psData.requestType !== "AMENDMENT") {
    fields.push("anticipatedPrimaryCompletionDt");
    fields.push("isCollectIrbApprovalPostClosure");
  }

  if (psData && formType === "REGULATORY" && psData.requestType === "NEW_SUBMISSION") {
    fields.push("countries");
  }

  if (psData && formType === "REGULATORY" && psData.requestType === "AMENDMENT") {
    fields.push("requiredByDt");
  }

  const isFormSoftValid = psData && softValidateForm(psData, fields);
  const shouldDispatchValidate = psData?.requestStatus === "DRAFT" && isFormSoftValid;

  // Include a fingerprint of relevant field values in the key
  // so SWR re-fetches when they change
  const fieldFingerprint = fields.map(f => psData?.[f] ?? "").join("|");

  const validationUrl = shouldDispatchValidate
    ? `${protocolSetupPath}/protocols/${psData.requestNumber}/request/${formType}/validate`
    : null;

  const swrKey = validationUrl ? [validationUrl, fieldFingerprint] : null;

  const { data, error, isLoading } = useSWR(swrKey, ([url]) => authFetcher(url), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0
  });

  return {
    isSubmitFormEnabled: data?.isValidReqNo,
    isValidating: isLoading,
    error,
    data
  };
}

export function useValidateCopyRequest(rqNum = null, formType = "OPEN_RAVE") {
  const validationUrl = validateProtocolSetupRequest(rqNum, formType);
  const { trigger, isMutating, error } = useSWRMutation(validationUrl, authFetcher, {
    onSuccess: (data) => {
      mutate(validationUrl, data, false);
    },
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0 // no polling
  });

  return {
    validateCopyRequest: trigger,
    isValidating: isMutating,
    error
  };
}



export const transformGridData = (useSWRNext) => (key, fetcher, config) => {
  return useSWRNext(
    key,
    async (...args) => {
      const data = await fetcher(...args);
      return {
        data: data?.data.map((d) => ({
          ...d,
          formPartType: d.formPartType === "OPEN_RAVE" ? "OPEN/Rave" : "Regulatory",
          requestTypeGroup: (() => {
            switch (d.requestType) {
              case "AMENDMENT":
                return "Amendment";
              case "CORRECTION":
                return "Revision";
              case "NON_AMENDMENT_REVISION":
                return "Revision";
              case "NEW_SUBMISSION":
                return "New";
              case "NEW_PROTOCOL":
                return "New";
              default:
                return d.requestType;
            }
          })()
        })),
        meta: data?.meta || {}
      };
    },
    config
  );
};

export const useFetchRequestBrowserData = () => {
  const { data, error, isLoading } = useSWR([requestBrowserPath, {}], authPostFetcher, {
    revalidateOnFocus: false,
    use: [transformGridData]
  });

  return {
    requestBrowserData: data,
    isLoading,
    error,
    mutateRequestBrowser: mutate
  };
};

export function useOEWGDate(protocolNumber, requestStatus) {
  const shouldFetch = !requestStatus || requestStatus === "NEW";

  const { data, error, isLoading } = useSWR(
    shouldFetch && protocolNumber ? `${protocolSetupPath}/part-a/${protocolNumber}/oewgdate` : null,
    authFetcher,
    {
      revalidateOnFocus: false
    }
  );

  return {
    oewgDate: data,
    isLoadingOEWGDate: isLoading,
    error
  };
}

export function useRequestHistory(id) {
  const shouldFetch = id;
  const { data, error, isLoading } = useSWR(
    shouldFetch ? `${esysDataServicePath}/esys/history/type/PROTSETUP/id/${id}` : null,
    authFetcher,
    {
      revalidateOnFocus: false
    }
  );

  const { trigger, isMutating } = useSWRMutation(
    shouldFetch ? `${esysDataServicePath}/esys/history/type/PROTSETUP/id/${id}` : null,
    authFetcher,
    {
      revalidateOnFocus: false
    }
  );

  return {
    requestHistory: data,
    isLoadingRequestHistory: isLoading,
    error,
    mutateRequestHistory: mutate,
    triggerRequestHistory: trigger,
    isMutatingRequestHistory: isMutating
  };
}
export function useStatusHistory(requestNumber) {
  const statusHistoryUrl = `${protocolSetupPath}/request/${requestNumber}/status/history`;

  const { data, error, isLoading } = useSWR(requestNumber ? statusHistoryUrl : null, authFetcher, {
    revalidateOnFocus: false
  });

  const { trigger, isMutating } = useSWRMutation(requestNumber ? statusHistoryUrl : null, authFetcher, {
    revalidateOnFocus: false
  });

  return {
    statusHistory: data || [],
    isLoadingStatusHistory: isLoading,
    error,
    mutateStatusHistory: mutate,
    triggerStatusHistory: trigger,
    isMutatingStatusHistory: isMutating
  };
}

export function useRelatedRequest(protocolNumber, lpo, otherFormType) {
  const shouldFetch = protocolNumber && lpo && otherFormType;

  const payload = {
    filter: [
      {
        field: "leadGrp",
        operation: "=",
        value: lpo
      },
      {
        field: "protocolNumber",
        operation: "=",
        value: protocolNumber
      },
      {
        field: "formPartType",
        operation: "=",
        value: otherFormType
      }
    ],
    first: 0,
    isMetaRequired: false,
    size: 0,
    sort: [

    ]
  };

  const { data, error, isLoading } = useSWR(
    shouldFetch ? [requestBrowserPath, payload] : null,
    authPostFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      use: [transformGridData]
    }
  );

  const relatedRequest = data?.data
    ?.filter(req => !["COMPLETED", "RETIRED", "CANCELED"].includes(req.requestStatus))
    ?.sort((a, b) => {
      // Get progression list without RETIRED and CANCELED for comparison
      const fullProgression = STATUS_PROGRESSION[otherFormType] || [];
      const progression = fullProgression.filter(status => !["RETIRED", "CANCELED"].includes(status));

      const indexA = progression.indexOf(a.requestStatus);
      const indexB = progression.indexOf(b.requestStatus);

      // If both statuses are in the progression, prioritize the most progressed (higher index)
      if (indexA !== -1 && indexB !== -1) {
        if (indexB !== indexA) {
          return indexB - indexA; // Higher index = more progressed (sort descending)
        }
      }

      // If only one status is in the progression, prioritize it
      if (indexA !== -1 && indexB === -1) return -1;
      if (indexA === -1 && indexB !== -1) return 1;

      // If both have the same status or neither is in progression, fall back to updatedDate
      const dateA = new Date(a.updatedDate);
      const dateB = new Date(b.updatedDate);
      return dateB - dateA; // Sort descending (most recent first)
    })?.[0]; // Get the most progressed record
  return {
    relatedRequest,
    isLoading,
    error
  };
}

export function useFindCurrentActiveRequest(request) {
  const { protocolNumber, lpo, formPartType } = request;
  const shouldFetch = protocolNumber && lpo && formPartType;

  const payload = {
    filter: [
      {
        field: "leadGrp",
        operation: "=",
        value: lpo
      },
      {
        field: "protocolNumber",
        operation: "=",
        value: protocolNumber
      },
      {
        field: "formPartType",
        operation: "=",
        value: formPartType
      }
    ],
    first: 0,
    isMetaRequired: false,
    size: 0,
    sort: []
  };

  const { data } = useSWR(
    shouldFetch ? [requestBrowserPath, payload] : null,
    authPostFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      use: [transformGridData]
    }
  );



  const activeRequest = data?.data
    ?.filter((req) => req.requestStatus !== "RETIRED" && req.requestStatus !== "INACTIVE")
    ?.sort((a, b) => {
      // Parse dates in format "DD-MMM-YYYY"
      const dateA = new Date(a.updatedDate);
      const dateB = new Date(b.updatedDate);
      return dateB - dateA; // Sort descending (most recent first)
    })?.[0];

  return activeRequest || request;
}


export const useFindCurrentRequest = (protocolNumber, formPartType, nextRequestNumber) => {
  const shouldFetch = protocolNumber && formPartType;
  const payload = {
    filter: [
      {
        field: "protocolNumber",
        operation: "=",
        value: protocolNumber
      },
      {
        field: "formPartType",
        operation: "=",
        value: formPartType
      }
    ],
    first: 0,
    isMetaRequired: false,
    size: 0,
    sort: []
  };

  const { data } = useSWR(
    shouldFetch ? [requestBrowserPath, payload] : null,
    authPostFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      use: [transformGridData]
    }
  );

  // Filter locally by requestNumber since server-side filtering is not working
  const filteredRequest = data?.data?.find(
    (req) => req.requestNumber === parseInt(nextRequestNumber, 10)
  );

  return filteredRequest || data;
};

